<template>
  <PColapsable>
    <template #title>
      <div class="titleColaps_ColapsableSalidasTable">
        <span class="cod-divisa-label">{{ divisa.codDivisa }}</span>
        <span class="divisa-total">{{ filterNumber(cantidadDisponible, 2) }}</span>
      </div>
    </template>
    <b-table
      :fields="visibleFields"
      :items="transferencias"
      :sort-by.sync="pagination.orderBy"
      :sort-desc.sync="sortDesc"
      sort-icon-left
      no-local-sorting
      :empty-text="$t('label.noData')"
      show-empty
      no-border-collapse
      borderless
      responsive
      @sort-changed="onSortingChange"
    >
      <div class="text-center loader my-2" slot="table-busy">
        <b-spinner class="align-middle" />
      </div>

      <template #head(add-columns)>
        <SelectColumns
          :fields.sync="fields"
          :pagination="pagination"
          :cod-table="columnsConfig ? columnsConfig.codTabla : ''"
        />
      </template>

      <!-- Left Actions Btns -->
      <template #cell(actions-left)="row">
        <div class="table-button-container d-flex">
          <b-icon
            class="icon-details ml-3 my-auto cursor-pointer"
            icon="chevron-down"
            aria-label="Collapse Toggle"
            :flip-v="row.detailsShowing"
            @click="row.toggleDetails"
          />
          <b-icon
            :id="`info-${tipo}-${divisa.codDivisa}-${row.index}`"
            class="icon-details ml-3 my-auto cursor-pointer"
            icon="exclamation-circle"
            :alt="$t('label.informacion')"
          />
          <b-tooltip
            :target="`info-${tipo}-${divisa.codDivisa}-${row.index}`"
            custom-class="table-cell-tooltip"
            placement="bottom"
          >
            <template v-for="(detalleOferta, index) in row.item.detallesOferta">
              <div :key="detalleOferta.id">
                <hr v-if="index" class="m-0" />

                <div class="tooltip-inner-content-diff">
                  <div class="title-info">
                    {{ $t("label.denominacion") }}:
                    {{ detalleOferta.soCalidadDenominacion.denominacion.codDenominacion }}
                    {{ detalleOferta.soCalidadDenominacion.desCalidad }}
                  </div>
                  <hr v-if="row.item.detallesOferta.length === 1" class="m-0 pt-2" />
                  <div>
                    {{ $t("label.ofertado") }}: {{ divisa.simbolo }}
                    {{ filterNumber(detalleOferta.cantidadOfertada, 2) }}
                  </div>
                  <div>
                    {{ $t("label.sinTransferir") }}: {{ divisa.simbolo }}
                    {{ filterNumber(detalleOferta.cantidadDisponible, 2) }}
                  </div>
                </div>
              </div>
            </template>
            <template v-for="(detalleDemanda, index) in row.item.detallesDemanda">
              <div :key="detalleDemanda.id">
                <hr v-if="index" class="m-0" />

                <div class="tooltip-inner-content-diff">
                  <div class="title-info">
                    {{ $t("label.denominacion") }}:
                    {{ detalleDemanda.soCalidadDenominacion.denominacion.codDenominacion }}
                    {{ detalleDemanda.soCalidadDenominacion.desCalidad }}
                  </div>
                  <hr v-if="row.item.detallesDemanda.length === 1" class="m-0 pt-2" />
                  <div>
                    {{ $t("label.demandado") }}: {{ divisa.simbolo }}
                    {{ filterNumber(detalleDemanda.cantidadDemandada, 2) }}
                  </div>
                  <div class="mb-2">
                    {{ $t("label.sinDemandar") }}: {{ divisa.simbolo }}
                    {{ filterNumber(detalleDemanda.cantidadDisponible, 2) }}
                  </div>
                </div>
              </div>
            </template>
          </b-tooltip>
          <b-icon
            v-if="row.item.estado === 'ACTIVA'"
            :id="`cancel-${tipo}-${divisa.codDivisa}-${row.index}`"
            class="icon-details ml-3 my-auto cursor-pointer"
            icon="x-circle"
            v-b-tooltip.hover.d300="$t('label.cancelar')"
            :alt="$t('label.cancelar')"
            @click="tipo === 'OFERTA' ? cancelarOferta(row.item.id) : cancelarDemanda(row.item.id)"
          />
          <b-icon
            v-if="showEditAction(row.item)"
            :class="[
              'icon-details ml-3 my-auto',
              enableEditAction(row.item) ? 'cursor-pointer' : 'icon-disabled',
            ]"
            icon="pencil-square"
            v-b-tooltip.hover.d300="
              enableEditAction(row.item) ? $t('label.modificar') : $t('label.no.modificable')
            "
            :alt="$t('label.modificar')"
            @click="enableEditAction(row.item) && editarOfertaOrDemanda(row.item)"
          />
          <div class="d-flex align-items-center ml-3" v-if="row.item.cantidadAplicada">
            <b-icon
              icon="check-square"
              class="icon-details"
              variant="secondary"
              v-b-tooltip.hover.d300="$t('label.cantidadAplicada')"
            />
            <span class="cant-aplicada-text">{{ row.item.cantidadAplicada }}</span>
          </div>
        </div>
      </template>

      <template #cell(estado)="row">
        <b-icon :class="`icon-estado ${row.value}`" icon="circle-fill" />
        {{ $t(`label.estado.${row.value}`) }}
      </template>

      <template #cell(coste)="row">
        {{ $t(`label.coste.${row.value.codParametro}`) }}
      </template>

      <template #cell(checkParcial)="row">
        {{ $t(`label.${row.value ? "yes" : "no"}`) }}
      </template>

      <template #row-details="row">
        <b-card class="detail-row">
          <DetalleTransferencia
            :ofertaDemanda="row.item"
            :tipo="tipo"
            @rechazarDemanda="rechazarDemanda"
            @cancelarDemandaPrivada="cancelarDemandaPrivada"
            @aceptarDemanda="aceptarDemanda"
          />
          <TransferenciasCompatibles
            v-if="row.item.estado === 'ACTIVA' && row.item.cantidadDisponible > 0"
            :ofertaDemanda="row.item"
            :tipo="tipo"
            @relacionarDemanda="relacionarDemanda"
            @relacionarOferta="relacionarOferta"
          />
        </b-card>
      </template>
    </b-table>
    <div class="mt-3" v-if="totalElements > pagination.size">
      <pagination
        :current-page="pagination.page"
        :page-size="pagination.size"
        :total-elements="totalElements"
        @change="onChangeCurrentPage"
      ></pagination>
    </div>
  </PColapsable>
</template>
<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { EDirection, IColumnaResponse, IPaged } from "@/services";
import { IConfColumnaPais, IDivisa, IField, IOfertaDemanda, ITransferencia } from "@/entities";
import SelectColumns from "@/components/common/SelectColumns/SelectColumns.vue";
import VueFilters from "@/vue-filters";
import DetalleTransferencia from "@/pages/transferencias/propias/DetalleTransferencia.vue";
import TransferenciasCompatibles from "@/pages/transferencias/propias/TransferenciasCompatibles.vue";
import Pagination from "@/components/common/Pagination.vue";

@Component({
  components: {
    Pagination,
    TransferenciasCompatibles,
    DetalleTransferencia,
    SelectColumns,
  },
  mixins: [VueFilters],
})
export class TransferenciasPropiasTable extends Vue {
  @Prop({ required: false, default: 0 }) totalElements!: number;
  @Prop({ required: true }) value!: IPaged;
  @Prop({ required: true }) transferencias!: IOfertaDemanda[];
  @Prop({ required: true }) divisa!: IDivisa;
  @Prop({ required: true }) tipo!: "OFERTA" | "DEMANDA";
  @Prop({ required: true }) cantidadDisponible!: number;
  @Prop({ required: true }) columnsConfig!: IColumnaResponse;

  public sortDesc = false;
  public pagination: IPaged = {
    direction: EDirection.ASC,
    orderBy: "fecCrear",
    totalElements: 0,
    page: 1,
    size: 10,
  };

  onSortingChange(): void {
    this.$nextTick(async () => {
      this.pagination.direction = this.sortDesc ? EDirection.DESC : EDirection.ASC;
      this.$emit("input", this.pagination);
      this.$emit("change", {
        action: "changeTable",
        data: { tipo: this.tipo, divisa: this.divisa },
      });
    });
  }
  isVisible(visibleKeys: string[], column: string): boolean {
    if (column == "tasa") {
      return (
        this.hasNumTasa() && (!visibleKeys.length || !!visibleKeys.find((key) => key === column))
      );
    } else if (column == "transportadora") {
      return (
        this.hasTransportadora() &&
        (!visibleKeys.length || !!visibleKeys.find((key) => key === column))
      );
    }
    return !visibleKeys.length || !!visibleKeys.find((key) => key === column);
  }
  hasNumTasa(): boolean {
    const hasNumTasaList: IOfertaDemanda[] = [];
    this.transferencias.forEach((transferencia) => {
      if (transferencia.numTasa != null) {
        hasNumTasaList.push(transferencia);
      }
    });
    return hasNumTasaList.length > 0;
  }
  hasNumTasaEntero(desCliente: string): boolean {
    for (let transferencia of this.transferencias) {
      if (
        transferencia.clientexdel.cliente.desCliente == desCliente &&
        transferencia.clientexdel.cliente.pais.hasTasaEnteros
      ) {
        return true;
      }
    }
    return false;
  }
  hasTransportadora(): boolean {
    const hasTransportadoraList: IOfertaDemanda[] = [];
    this.transferencias.forEach((transferencia) => {
      if (transferencia.transportadora?.id != null) {
        hasTransportadoraList.push(transferencia);
      }
    });
    return hasTransportadoraList.length > 0;
  }
  cancelarOferta(id: string): void {
    this.$emit("cancelOferta", { action: "cancelOferta", data: id });
  }
  cancelarDemanda(id: string): void {
    this.$emit("cancelDemanda", { action: "cancelDemanda", data: id });
  }
  rechazarDemanda(id: number): void {
    this.$emit("rechazarDemanda", { action: "rechazarDemanda", data: id });
  }
  cancelarDemandaPrivada(id: number): void {
    this.$emit("cancelarDemandaPrivada", { action: "cancelarDemandaPrivada", data: id });
  }
  aceptarDemanda(transferencia: ITransferencia): void {
    this.$emit("aceptarDemanda", { action: "aceptarDemanda", data: transferencia });
  }
  relacionarDemanda(data: { demandaCompatible: IOfertaDemanda; oferta: IOfertaDemanda }): void {
    this.$emit("relacionarDemanda", { action: "relacionarDemanda", data: data });
  }
  relacionarOferta(data: { ofertaCompatible: IOfertaDemanda; demanda: IOfertaDemanda }): void {
    this.$emit("relacionarOferta", { action: "relacionarOferta", data: data });
  }

  onChangeCurrentPage(currentPage: number): void {
    this.pagination.page = currentPage;
    this.$emit("input", this.pagination);
    this.$emit("change", { action: "changeTable", data: { tipo: this.tipo, divisa: this.divisa } });
  }

  get fields(): IField[] {
    const visibleKeys = this.columnsConfig?.codColumna?.split(",").filter((s) => s !== "") || [];
    let columns: IField[] = [
      {
        key: "actions-left",
        label: this.$t("label.acciones"),
        sortable: false,
        thClass: "table-header table-header-active pl-4",
        tdClass: "table-cell-first actions",
        visible: true,
        class: "table-cell",
        aux: true,
      },
    ];
    let hasNumTasaEntero = false;
    this.columnsConfig.columnas?.forEach((column: IConfColumnaPais) => {
      columns.push({
        key: column.keyFront,
        label: this.$t("label." + column.codColumna),
        sortable: true,
        thClass: "table-header",
        tdClass: "table-cell-middle",
        thStyle: column.widthFront ? { width: column.widthFront } : {},
        visible: this.isVisible(visibleKeys, column.codColumna),
        class: "table-cell",
        aux: false,
        code: column.codColumna,
        formatter: (value: any): string => {
          if (column.formatType == "Date") {
            return this.filterDate(value) + " - " + this.filterTime(value);
          } else if (column.formatType == "Number") {
            return this.filterNumber(value, 2);
          }
          if (column.codColumna == "cliente") {
            hasNumTasaEntero = this.hasNumTasaEntero(value);
          }
          if (column.keyFront == "numTasa") {
            return hasNumTasaEntero ? Math.round(value * 100000) + " x 10.000" : value + " %";
          }
          return value;
        },
      });
    });
    columns.push({
      key: "add-columns",
      label: "",
      sortable: false,
      thClass: "table-header table-header-add-columnas",
      tdClass: "table-cell table-add-columns",
      visible: true,
      aux: true,
    });
    return columns;
  }

  set fields(fields: IField[]) {
    this.columnsConfig.codColumna =
      fields
        .filter((item) => item.visible && !item.aux)
        .map((item) => item.code)
        .join(",") || "";
  }

  get visibleFields(): IField[] {
    return this.fields.filter((item) => item.visible);
  }

  showEditAction(ofertaDemanda: IOfertaDemanda): boolean {
    const hasEnableEditAction = ofertaDemanda.clientexdel.cliente.pais.editOfertasDemandas;
    return hasEnableEditAction;
  }

  enableEditAction(ofertaDemanda: IOfertaDemanda): boolean {
    return (ofertaDemanda.estado === "ACTIVA" && ofertaDemanda.isUpdatable) || false;
  }

  public editarOfertaOrDemanda(ofertaDemanda: IOfertaDemanda): void {
    this.$emit("editarOfertaOrDemanda", { action: `editar${this.tipo}`, data: ofertaDemanda });
  }
}
export default TransferenciasPropiasTable;
</script>
<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}
.toggle-separator {
  border-top: solid 1px #e9e9e9;
}
.info-separator {
  border-top: solid 1px #b7b7b7;
  padding-top: 9px;
}
.title-info {
  padding-top: 6px;
  padding-bottom: 6px;
}
.cod-divisa-label {
  margin-left: 5px;
  margin-right: 9px;
  font-size: 14px;
  font-weight: 300;
  color: var(--secundario-prosegur-claro);
}

.divisa-total {
  font-size: 14px;
  font-weight: bold;
  color: var(--secundario-prosegur-claro);
}
.icon-estado {
  width: 6px;
  height: 6px;
  margin: 0 4px 1px 0;
  vertical-align: initial;
  &.ACTIVA {
    color: var(--grafico-1);
  }
  &.EXPIRADA {
    color: var(--accion-pendiente);
  }
  &.CANCELADA {
    color: var(--accion-error-alerta);
  }
  &.COMPLETADA {
    color: var(--accion-resuelto);
  }
}
.icon-disabled {
  color: #ccc;
  cursor: pointer;
}

.cant-aplicada-text {
  margin-left: 5px;
}
</style>
